import 'src/app/shared/leaflet-extensions/TileLayerGeoJSON/TileLayerGeoJSON.js';

import * as L from 'leaflet';
import { environment } from 'src/environments/environment';

import { BaseMapMode } from './base-map-mode';

export interface BaseMapProvider {
    id: string;
    name: string;
    mapServiceNames: string[];
    baseMapModes: BaseMapMode[];
    createBaseMapLayer(mapMode: BaseMapMode): L.TileLayer;
}

// ============================================================================
// BASE MAP CONSTANTS
// ============================================================================

/** Base map tile size in pixels (256px is standard, satellite only supports 256px) */
const BASE_MAP_TILE_SIZE = 256;

/** Zoom offset for base map tiles (0 for 256px tiles) */
const BASE_MAP_ZOOM_OFFSET = 0;

/** Maximum native zoom level supported by the tile provider */
const BASE_MAP_MAX_NATIVE_ZOOM = 20;

/** Minimum zoom level for base maps */
const BASE_MAP_MIN_ZOOM = 1;

/** Maximum zoom level for base maps (allows over-zoom beyond native) */
const BASE_MAP_MAX_ZOOM = 22;

/** Z-index for base map layer (very low to ensure it's behind all other layers) */
const BASE_MAP_Z_INDEX = -9999998;

// ============================================================================

export enum TrimbleBaseMapMode {
    MAP = 'map',
    MAP_DARKNESS = 'mapDarkness',
    MAP_LIGHTNESS = 'mapLightness',
    MAP_TERRAIN = 'mapTerrain',
    DIGITAL_GLOBE = 'digitalGlobe'
}

export class TrimbleBaseMapProvider {
    id = 'trimble';
    name = 'Trimble';
    mapServiceNames = ['Trimble Mapview', 'DigitalGlobe Imagery'];

    // get updated/completed from the Trimble Data Services API response
    baseMapModes: BaseMapMode[] = [
        {
            id: TrimbleBaseMapMode.MAP,
            label: 'MapViewer.BaseMap.Street.Label',
            externalName: 'Trimble Mapview',
            name: 'Map',
            data: '',
            maxNativeZoom: BASE_MAP_MAX_NATIVE_ZOOM,
            minZoom: BASE_MAP_MIN_ZOOM,
            maxZoom: BASE_MAP_MAX_ZOOM,
            imageUrl: 'assets/images/maps/trimble-map.jpg',
            selectedIconClass: 'trimble-map-icon',
            attribution: '<a href="https://maps.trimble.com/copyrights/" target="_blank">Trimble Maps</a>',
            tileUrl: environment.mapTileUrl,
            tileSize: BASE_MAP_TILE_SIZE,
            zoomOffset: BASE_MAP_ZOOM_OFFSET,
            subDomains: []
        },
        {
            id: TrimbleBaseMapMode.MAP_LIGHTNESS,
            label: 'MapViewer.BaseMap.StreetLight.Label',
            externalName: 'Trimble Mapview',
            name: 'Map Light',
            data: '',
            maxNativeZoom: BASE_MAP_MAX_NATIVE_ZOOM,
            minZoom: BASE_MAP_MIN_ZOOM,
            maxZoom: BASE_MAP_MAX_ZOOM,
            imageUrl: 'assets/images/maps/trimble-map.jpg',
            selectedIconClass: 'trimble-map-icon',
            attribution: '<a href="https://maps.trimble.com/copyrights/" target="_blank">Trimble Maps</a>',
            tileUrl: `${environment.mapTileUrl}&style=lightness`,
            tileSize: BASE_MAP_TILE_SIZE,
            zoomOffset: BASE_MAP_ZOOM_OFFSET,
            subDomains: []
        },
        {
            id: TrimbleBaseMapMode.MAP_DARKNESS,
            label: 'MapViewer.BaseMap.StreetDark.Label',
            externalName: 'Trimble Mapview',
            name: 'Map Dark',
            data: '',
            maxNativeZoom: BASE_MAP_MAX_NATIVE_ZOOM,
            minZoom: BASE_MAP_MIN_ZOOM,
            maxZoom: BASE_MAP_MAX_ZOOM,
            imageUrl: 'assets/images/maps/trimble-map.jpg',
            selectedIconClass: 'trimble-map-icon',
            attribution: '<a href="https://maps.trimble.com/copyrights/" target="_blank">Trimble Maps</a>',
            tileUrl: `${environment.mapTileUrl}&style=darkness`,
            tileSize: BASE_MAP_TILE_SIZE,
            zoomOffset: BASE_MAP_ZOOM_OFFSET,
            subDomains: []
        },
        {
            id: TrimbleBaseMapMode.MAP_TERRAIN,
            label: 'TC.Common.Terrain',
            externalName: 'Trimble Mapview',
            name: 'Map Terrain',
            data: '',
            maxNativeZoom: BASE_MAP_MAX_NATIVE_ZOOM,
            minZoom: BASE_MAP_MIN_ZOOM,
            maxZoom: BASE_MAP_MAX_ZOOM,
            imageUrl: 'assets/images/maps/trimble-map.jpg',
            selectedIconClass: 'trimble-map-icon',
            attribution: '<a href="https://maps.trimble.com/copyrights/" target="_blank">Trimble Maps</a>',
            tileUrl: `${environment.mapTileUrl}&style=terrain`,
            tileSize: BASE_MAP_TILE_SIZE,
            zoomOffset: BASE_MAP_ZOOM_OFFSET,
            subDomains: []
        },
        {
            id: TrimbleBaseMapMode.DIGITAL_GLOBE,
            label: 'TC.Common.Satellite',
            externalName: 'DigitalGlobe Imagery',
            name: 'Satellite',
            data: '',
            maxNativeZoom: BASE_MAP_MAX_NATIVE_ZOOM,
            minZoom: BASE_MAP_MIN_ZOOM,
            maxZoom: BASE_MAP_MAX_ZOOM,
            imageUrl: 'assets/images/maps/trimble-digital-globe.jpg',
            selectedIconClass: 'trimble-digital-globe-icon',
            attribution: '<a href="https://maps.trimble.com/copyrights/" target="_blank">Trimble Maps</a>',
            tileUrl: `${environment.mapTileUrl}&style=satellite&imgsrc=sat2`,
            // Satellite (DigitalGlobe) tiles only support 256px - PCMiler API returns 400 for 512px satellite tiles
            tileSize: BASE_MAP_TILE_SIZE,
            zoomOffset: BASE_MAP_ZOOM_OFFSET,
            subDomains: []
        }
    ];

    public createBaseMapLayer(mapMode: BaseMapMode): L.TileLayer {
        // TODO: TrimbleData parallel subdomains is incompatible with leaflet because of differing Signature param per subdomain URL
        // Add width parameter to the tile URL only for non-default tile sizes (256 is default, no width param needed)
        let tileUrl = mapMode.tileUrl;
        if (mapMode.tileSize !== 256) {
            tileUrl = mapMode.tileUrl.includes('?')
                ? `${mapMode.tileUrl}&width=${mapMode.tileSize}`
                : `${mapMode.tileUrl}?width=${mapMode.tileSize}`;
        }
        return new L.TileLayer(tileUrl, {
            subdomains: mapMode.subDomains,
            attribution: mapMode.attribution,
            maxZoom: mapMode.maxZoom,
            minZoom: mapMode.minZoom,
            maxNativeZoom: mapMode.maxNativeZoom,
            tileSize: mapMode.tileSize,
            zoomOffset: mapMode.zoomOffset,
            zIndex: BASE_MAP_Z_INDEX
        });
    }
}
